import React, { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import "./Cart.scss";
import { useSelector, useDispatch } from "react-redux";
import { removeItem, updateQuantity, resetCart, removeUnavailable } from "../../Store/reducers/cartReducer";
import useCartStockSync from "../../hooks/useCartStockSync";
import ProductPrice from "../../pages/ProductPrice/ProductPrice";
import { useNavigate } from "react-router-dom";

import { useAuth } from '../../hooks/Services/AuthContext';
import { fetchCart, saveCart } from '../../Store/reducers/cartReducer';
import Cookies from 'js-cookie';
import CheckoutModal from "../../pages/Orders/Checkout/CheckoutModal";
import FreeShippingProgress from "../FreeShipping/FreeShippingProgress";
import { computeTotals } from "../../config/checkout";

// How long the bag stays open after an item is added elsewhere on the page.
const ADDED_PREVIEW_MS = 3500;

// Mini-bag dropdown. Rendered inside the navbar's cart icon wrapper, which is
// position: relative, so the panel drops down right under the icon.
const Cart = ({ open, onClose, onOpen }) => {
    const products = useSelector((state) => state.cart.products);
    const discount = useSelector((state) => state.cart.discount);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const intl = useIntl();
    const [showCheckoutModal, setShowCheckoutModal] = useState(false);
    const panelRef = useRef(null);
    const hoveredRef = useRef(false);
    const prevCountRef = useRef(null);

    const { token } = useAuth();

    // Fetch cart after login or token available
    useEffect(() => {
        if (token) {
            dispatch(fetchCart(token));
        }
    }, [token, dispatch]);

    // Save cart on every change
    useEffect(() => {
        if (token) {
            dispatch(saveCart({ token, products }));
        }
    }, [products, token, dispatch]);

    const count = products.reduce((sum, item) => sum + item.quantity, 0);

    // Briefly reveal the bag when something new is added, like boutique sites do.
    useEffect(() => {
        const prev = prevCountRef.current;
        prevCountRef.current = count;
        if (prev === null || count <= prev || !onOpen) return;
        onOpen();
        const timer = setTimeout(() => {
            if (!hoveredRef.current) onClose();
        }, ADDED_PREVIEW_MS);
        return () => clearTimeout(timer);
    }, [count]); // eslint-disable-line react-hooks/exhaustive-deps

    // Close on outside click / Escape. The cart icon itself toggles the panel,
    // so clicks inside the wrapper are ignored here.
    useEffect(() => {
        if (!open) return;
        const handlePointer = (e) => {
            const wrapper = panelRef.current?.parentElement;
            if (wrapper && !wrapper.contains(e.target) && !showCheckoutModal) onClose();
        };
        const handleKey = (e) => e.key === "Escape" && onClose();
        document.addEventListener("mousedown", handlePointer);
        document.addEventListener("keydown", handleKey);
        return () => {
            document.removeEventListener("mousedown", handlePointer);
            document.removeEventListener("keydown", handleKey);
        };
    }, [open, onClose, showCheckoutModal]);

    // Fresh stock every time the bag opens.
    useCartStockSync(open, open);
    const hasUnavailable = products.some((item) => item.unavailable);
    const totalPrice = computeTotals(products, null).subtotal;

    const changeQuantity = (item, delta) => {
        const next = item.quantity + delta;
        if (next < 1) return dispatch(removeItem(item.id));
        if (item.stock != null && next > item.stock) return;
        dispatch(updateQuantity({ id: item.id, quantity: next }));
    };

    const handleCheckoutClick = () => {
        if (!Cookies.get("token")) {
            // Not logged in - show modal
            setShowCheckoutModal(true);
        } else {
            onClose();
            navigate("/checkout");
        }
    };

    const goShopping = () => {
        onClose();
        navigate("/products");
    };

    return (
        <>
            <div
                ref={panelRef}
                className={`mini-bag ${open ? "is-open" : ""}`}
                role="dialog"
                aria-label={intl.formatMessage({ id: "cart" })}
                aria-hidden={!open}
                onMouseEnter={() => { hoveredRef.current = true; }}
                onMouseLeave={() => { hoveredRef.current = false; }}
            >
                <div className="mini-bag__header">
                    <span className="mini-bag__title">
                        <FormattedMessage id="your_cart_products" defaultMessage="Twoje produkty" />
                    </span>
                    <span className="mini-bag__count">{count}</span>
                </div>

                {products.length === 0 ? (
                    <div className="mini-bag__empty">
                        <p><FormattedMessage id="cart_is_empty" defaultMessage="Twój koszyk jest pusty." /></p>
                        <button className="btn btn--outline" onClick={goShopping}>
                            <FormattedMessage id="continue_shopping" defaultMessage="Kontynuuj zakupy" />
                        </button>
                    </div>
                ) : (
                    <>
                        <ul className="mini-bag__items">
                            {products.map((item) => (
                                <li className={`mini-bag__item${item.unavailable ? " is-unavailable" : ""}`} key={item.id}>
                                    <div className="mini-bag__thumb">
                                        <img src={process.env.REACT_APP_UPLOAD_URL + item.img} alt={item.title} loading="lazy" />
                                    </div>
                                    <div className="mini-bag__details">
                                        <span className="mini-bag__name">{item.title}</span>
                                        {item.unavailable ? (
                                            <span className="mini-bag__unavailable">
                                                <FormattedMessage id="out_of_stock_short" />
                                            </span>
                                        ) : (
                                        <div className="mini-bag__qty" aria-label={intl.formatMessage({ id: "quantity" })}>
                                            <button onClick={() => changeQuantity(item, -1)} aria-label="-">−</button>
                                            <span>{item.quantity}</span>
                                            <button
                                                onClick={() => changeQuantity(item, 1)}
                                                disabled={item.stock != null && item.quantity >= item.stock}
                                                aria-label="+"
                                            >+</button>
                                        </div>
                                        )}
                                        <button className="mini-bag__remove" onClick={() => dispatch(removeItem(item.id))}>
                                            <FormattedMessage id="remove_item" defaultMessage="Usuń" />
                                        </button>
                                    </div>
                                    <span className="mini-bag__price">
                                        {!item.unavailable && <ProductPrice basePrice={item.new_price * item.quantity} />}
                                    </span>
                                </li>
                            ))}
                        </ul>

                        <div className="mini-bag__footer">
                            <div className="mini-bag__total">
                                <span><FormattedMessage id="subtotal" /></span>
                                <span><ProductPrice basePrice={totalPrice.toFixed(2)} /></span>
                            </div>
                            <FreeShippingProgress
                                amount={computeTotals(products, discount).discounted}
                                sx={{ my: 1.5 }}
                            />
                            {hasUnavailable && (
                                <p className="mini-bag__note mini-bag__note--warning">
                                    <FormattedMessage id="cart_has_unavailable" />{" "}
                                    <button type="button" onClick={() => dispatch(removeUnavailable())}>
                                        <FormattedMessage id="cart_remove_unavailable" />
                                    </button>
                                </p>
                            )}
                            <button
                                className="btn btn--solid"
                                id="checkout"
                                onClick={handleCheckoutClick}
                                disabled={hasUnavailable}
                            >
                                <FormattedMessage id="checkout" />
                            </button>
                            <div className="mini-bag__links">
                                <button onClick={goShopping}>
                                    <FormattedMessage id="continue_shopping" defaultMessage="Kontynuuj zakupy" />
                                </button>
                                <button id="reset" onClick={() => dispatch(resetCart())}>
                                    <FormattedMessage id="reset_cart" />
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>

            <CheckoutModal
                open={showCheckoutModal}
                onClose={() => setShowCheckoutModal(false)}
            />
        </>
    );
};

export default Cart;
